<template>
    <div class="settings-container">
        <h2 class="page-title">계정 및 시스템 설정</h2>

        <div class="settings-grid">
            <!-- 비밀번호 변경 카드 -->
            <div class="settings-card">
                <h3 class="card-title">관리자 비밀번호 변경</h3>
                <div class="form">
                    <div class="row">
                        <label for="pc-id">아이디</label>
                        <input id="pc-id" v-model="userId" type="text" autocomplete="username"/>
                    </div>
                    <div class="row">
                        <label for="pc-password">현재 비밀번호</label>
                        <input id="pc-password" v-model="password" type="password" autocomplete="current-password"/>
                    </div>
                    <div class="row">
                        <label for="pc-new-password">새 비밀번호</label>
                        <input id="pc-new-password" v-model="newPassword" type="password" autocomplete="new-password"/>
                    </div>
                    <div v-if="message" :class="['message', messageType]">{{ message }}</div>
                    <div class="row button-row">
                        <button type="button" class="action-btn" @click="changePassword">비밀번호 변경</button>
                    </div>
                </div>
            </div>

            <!-- 데이터 백업 카드 -->
            <div class="settings-card">
                <h3 class="card-title">시스템 데이터 수동 백업</h3>
                <p class="desc">
                    현재 상용 서버의 전체 데이터(데이터베이스 및 업로드된 시공후기·문의사항 이미지 에셋)를 Object Storage로 백업합니다.<br>
                    이 작업은 서버 백그라운드에서 실행되며, 완료 시점까지 수 분이 소요될 수 있습니다.
                </p>
                
                <div class="backup-actions">
                    <button 
                        type="button" 
                        class="action-btn backup-btn" 
                        @click="triggerBackup" 
                        :disabled="isBackingUp"
                    >
                        {{ isBackingUp ? '백업 진행 중...' : '상용 서버 데이터 백업 시작' }}
                    </button>
                </div>

                <!-- 백업 상태 표시 -->
                <div v-if="isBackingUp" class="status-container">
                    <div class="spinner"></div>
                    <p class="status-text">서버에서 데이터 압축 및 Object Storage 업로드를 진행하고 있습니다.</p>
                    <p class="warning-text">⚠️ 페이지를 벗어나더라도 백업은 백그라운드에서 계속 진행됩니다.</p>
                </div>
            </div>
        </div>

        <!-- 데이터 초기화 카드 (전체 너비) -->
        <div class="settings-card danger-card">
            <h3 class="card-title danger-title">⚠️ 시스템 데이터 초기화 (STG 전용)</h3>
            <p class="desc">
                현재 서버의 <strong>모든 데이터를 완전히 삭제</strong>합니다. 이 작업은 되돌릴 수 없습니다.<br>
                삭제 대상: 시공후기·문의하기 게시글, 댓글, 아파트 시공 사진, 메인홈 사진, 관리자 비밀번호 (기본값으로 리셋)
            </p>
            <div class="backup-actions">
                <button
                    type="button"
                    class="action-btn reset-btn"
                    @click="resetData"
                    :disabled="isResetting"
                >
                    {{ isResetting ? '초기화 진행 중...' : '전체 데이터 초기화' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { adminApi } from '@/store'

@Component
export default class Settings extends Vue {
    // 비밀번호 변경 관련 상태
    userId = ''
    password = ''
    newPassword = ''
    message = ''
    messageType = 'success'

    // 백업 관련 상태
    isBackingUp = false
    backupPolling = null

    // 리셋 관련 상태
    isResetting = false

    async beforeMount() {
        // 기존 백업 상태를 조회하여 이미 실행 중인지 확인
        try {
            const res = await adminApi.get('/backup/status');
            if (res.data.isBackingUp) {
                this.isBackingUp = true;
                this.startBackupPolling();
            }
        } catch (e) {
            console.error('백업 상태 로드 실패:', e);
        }
    }

    async changePassword () {
        this.message = ''
        if (!this.userId || !this.password || !this.newPassword) {
            this.messageType = 'error';
            this.message = '모든 필드를 입력해 주세요.';
            return;
        }
        try {
            await this.$store.dispatch('changePassword', {
                id: this.userId,
                password: this.password,
                newPassword: this.newPassword
            })
            this.messageType = 'success'
            this.message = '비밀번호가 성공적으로 변경되었습니다.'
            this.password = ''
            this.newPassword = ''
        } catch (error) {
            this.messageType = 'error'
            this.message = (error.response && error.response.data && error.response.data.message)
                ? error.response.data.message
                : '비밀번호 변경에 실패했습니다.'
        }
    }

    async triggerBackup() {
        if (!confirm('현재 상용 서버의 전체 데이터를 Object Storage로 즉시 백업하시겠습니까?\n(수 분이 소요될 수 있습니다)')) return;

        this.isBackingUp = true;
        try {
            await adminApi.post('/backup/trigger');
            alert('백업 프로세스가 백그라운드에서 시작되었습니다.');
            this.startBackupPolling();
        } catch (e) {
            alert('백업 시작 실패: ' + e.message);
            this.isBackingUp = false;
        }
    }

    startBackupPolling() {
        if (this.backupPolling) clearInterval(this.backupPolling);
        this.backupPolling = setInterval(async () => {
            try {
                const res = await adminApi.get('/backup/status');
                if (!res.data.isBackingUp) {
                    clearInterval(this.backupPolling);
                    this.isBackingUp = false;

                    if (res.data.lastBackupResult && res.data.lastBackupResult.success) {
                        alert('전체 데이터 백업이 성공적으로 완료되었습니다!\n\n' + res.data.lastBackupResult.message);
                    } else {
                        const errMsg = res.data.lastBackupResult ? res.data.lastBackupResult.message : '알 수 없는 오류가 발생했습니다.';
                        alert('백업 과정에서 문제가 발생했습니다:\n\n' + errMsg);
                    }
                }
            } catch (e) {
                console.error('백업 상태 확인 오류:', e);
            }
        }, 5000);
    }

    beforeDestroy() {
        if (this.backupPolling) {
            clearInterval(this.backupPolling);
        }
    }

    async resetData() {
        if (!confirm('⚠️ 경고\n\n현재 서버의 모든 데이터(사진, 게시글, 댓글, 비밀번호 등)가 삭제됩니다.\n\n계속하시겠습니까?')) return;
        if (!confirm('⚠️ 최종 확인\n\n정말로 초기화하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) return;

        this.isResetting = true;
        try {
            const res = await adminApi.post('/backup/reset');
            if (res.data.success) {
                alert('✅ ' + res.data.message);
                // 비밀번호가 초기화되었으므로 로그아웃 처리
                this.$store.dispatch('logout');
                this.$router.push({ name: 'admin' });
            } else {
                alert('❌ ' + res.data.message);
            }
        } catch (e) {
            alert('초기화 요청 실패: ' + (e.response?.data?.message || e.message));
        } finally {
            this.isResetting = false;
        }
    }
}
</script>

<style scoped lang="scss">
@import 'src/assets/style/common';
@import 'src/assets/style/media-query';

.settings-container {
    padding: 20px 0;
    
    .page-title {
        color: $dark-theme;
        font-size: 24px;
        margin-bottom: 30px;
        text-align: left;
    }
}

.settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    
    @include mobile {
        grid-template-columns: 1fr;
    }
}

.settings-card {
    background: #ffffff;
    border: 1px solid $transparent-dark-theme;
    border-radius: 8px;
    padding: 25px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
    
    .card-title {
        color: $dark-theme;
        font-size: 18px;
        margin-top: 0;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 1px solid $transparent-dark-theme;
    }
    
    .desc {
        color: #666;
        font-size: 13px;
        line-height: 1.6;
        margin-bottom: 20px;
    }
}

.form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    
    .row {
        display: flex;
        align-items: center;
        
        label {
            width: 100px;
            color: $dark-theme;
            font-size: 14px;
        }
        
        input {
            flex: 1;
            border: 1px solid $transparent-dark-theme;
            padding: 8px 12px;
            color: $dark-theme;
            border-radius: 4px;
            font-size: 14px;
            outline: none;
            
            &:focus {
                border-color: $dark-theme;
            }
        }
    }
    
    .button-row {
        justify-content: flex-end;
        margin-top: 10px;
    }
}

.action-btn {
    padding: 10px 20px;
    background: $dark-theme;
    color: $bright-theme;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    transition: background 0.2s;
    
    &:hover {
        background: #333333;
    }
    
    &:disabled {
        background: #cccccc;
        cursor: not-allowed;
        color: #666666;
    }
}

.backup-btn {
    background: #007bff;
    color: white;
    
    &:hover:not(:disabled) {
        background: #0069d9;
    }
}

.message {
    font-size: 13px;
    padding: 5px 0;
    
    &.success {
        color: #28a745;
    }
    
    &.error {
        color: #dc3545;
    }
}

.status-container {
    margin-top: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 4px;
    border-left: 4px solid #ffc107;
    text-align: center;
    
    .spinner {
        display: inline-block;
        width: 24px;
        height: 24px;
        border: 3px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        border-top-color: #ffc107;
        animation: spin 1s ease-in-out infinite;
        margin-bottom: 8px;
    }
    
    .status-text {
        font-weight: bold;
        color: #333;
        margin: 5px 0;
        font-size: 13px;
    }
    
    .warning-text {
        font-size: 11px;
        color: #666;
        margin: 3px 0 0 0;
    }
}

.danger-card {
    margin-top: 30px;
    border-color: #dc3545;
    background: #fff5f5;
    
    .danger-title {
        color: #dc3545;
        border-bottom-color: rgba(220, 53, 69, 0.2);
    }
}

.reset-btn {
    background: #dc3545;
    color: white;
    
    &:hover:not(:disabled) {
        background: #c82333;
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
